import React, { useState, useMemo, useEffect } from "react"
import { Button, Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import {
  FaPlus,
  FaPen,
  FaSearch,
  FaEye,
  FaChevronCircleLeft,
  FaChevronCircleRight,
} from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import Swal from "sweetalert2"
import Breadcrumb from "components/Common/Breadcrumb"
import RoleModal from "./RoleModal"
import RolesImg from "../../assets/images/shield.jpg"
import moment from "moment"
import Loader from "components/Loader/loader"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const RolesIndex = () => {
  const { checkAccess } = usePermissionData()

  const [roles, setRoles] = useState([])

  const [searchString, setSearchString] = useState("")
  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [modalType, setModalType] = useState("")

  const [loading, setLoading] = useState(false)

  const [roleData, setRoleData] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)

  const toggleModal = () => setIsModalOpen(!isModalOpen)

  const handleChange = e => {
    const { name, value } = e.target
    setRoleData(prev => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    getRolesData()
  }, [])

  async function getRolesData() {
    const APIdata = await fetchData(setLoading, "roles-and-permissions")
    if (APIdata?.success) {
      setRoles(APIdata?.data?.reverse())
    } else {
      Swal.fire({
        title: "Error Fetching Roles and Permissions",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleSave = async modulesState => {
    // Convert the modulesState to a permission string (if needed)
    const permissions = modulesState.map(
      m => `${m.id},${m.viewglobal},${m.view},${m.add},${m.edit},${m.delete}`
    )

    // Attach permissions to roleData before sending to the API
    const updatedRoleData = {
      ...roleData,
      permissions: JSON.stringify(permissions),
    }

    if (roleData.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "roles-and-permissions",
        1,
        updatedRoleData
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Role Updated Successfully.",
          icon: "success",
        })
        getRolesData()
      } else {
        Swal.fire({
          title: "Something Went Wrong",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(
        setLoading,
        "roles-and-permissions",
        updatedRoleData
      )
      if (APIdata?.data) {
        Swal.fire({
          title: "Role Added Successfully.",
          icon: "success",
        })
        getRolesData()
      } else {
        Swal.fire({
          title: "Something Went Wrong",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }

    toggleModal()
  }

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this Role",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3d6eb6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Delete it",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIData = await FetchDeleteData(
          setLoading,
          "roles-and-permissions",
          id
        )
        if (APIData?.data?.success) {
          Swal.fire({
            title: "Service Deleted Successfully.",
            icon: "success",
          })
          getRolesData()
        } else {
          Swal.fire({
            title: "Something Went Wrong",
            text: APIData?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  // 🔎 Filter roles
  const filteredRoles = useMemo(() => {
    return roles.filter(role =>
      role.roleName.toLowerCase().includes(searchString.toLowerCase())
    )
  }, [roles, searchString])

  // 📄 Pagination
  const indexOfLastItem = currentPage * perPageItem
  const indexOfFirstItem = indexOfLastItem - perPageItem
  const currentRoles = filteredRoles.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredRoles.length / perPageItem)

  return (
    <div className="page-content">
      <PermittedComponent isAccess={checkAccess(10, 1) || checkAccess(10, 2)}>
        {loading && <Loader />}
        <Container fluid>
          <Breadcrumb
            title="Dashboard"
            breadcrumbItem="Roles"
            description="Manage the Roles and Permissions for Users from Here."
          />

          <Row className="justify-content-between">
            <Col lg="4" className="text-center">
              <img
                src={RolesImg}
                className="w-100 object-fit-cover shadow-lg"
              />
            </Col>
            <Col lg="8" className="px-0">
              <Card className="shadow-lg">
                <CardBody>
                  {/* Header Controls */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-3">
                      <select
                        className="form-select rounded-4 ps-3 pe-5 w-auto"
                        value={perPageItem}
                        onChange={e => {
                          setPerPageItem(Number(e.target.value))
                          setCurrentPage(1)
                        }}
                      >
                        <option value={10}>Show 10</option>
                        <option value={20}>Show 20</option>
                        <option value={50}>Show 50</option>
                      </select>

                      <div className="position-relative">
                        <input
                          className="form-control rounded-4 px-3 pe-5"
                          placeholder="Search Role"
                          value={searchString}
                          onChange={e => {
                            setSearchString(e.target.value)
                            setCurrentPage(1)
                          }}
                        />
                        <span className="position-absolute end-0 top-50 translate-middle-y pe-3 text-muted">
                          <FaSearch />
                        </span>
                      </div>
                    </div>
                    {checkAccess(10, 3) && (
                      <div className="d-flex gap-3">
                        <Button
                          className="btn-blue"
                          onClick={() => {
                            setRoleData({})
                            toggleModal()
                            setModalType("")
                          }}
                        >
                          <FaPlus className="me-2" /> Role
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Roles Table */}
                  <div className="mt-5">
                    <Table responsive hover>
                      <thead className="bg-blue text-white">
                        <tr>
                          <th>#</th>
                          <th>Role Name</th>
                          <th className="text-center">Created At</th>
                          <th className="text-center">Last Updated At</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRoles.length > 0 ? (
                          currentRoles.map((role, index) => (
                            <tr key={role.id}>
                              <td>{indexOfFirstItem + index + 1}</td>
                              <td>{role.roleName}</td>
                              <td className="text-center">
                                {moment(role.createdAt).format(
                                  "DD MMM YYYY | hh:mm A"
                                )}
                              </td>
                              <td className="text-center">
                                {moment(role.updatedAt).format(
                                  "DD MMM YYYY | hh:mm A"
                                )}
                              </td>
                              <td className="d-flex justify-content-center">
                                <Button
                                  size="sm"
                                  className="me-2 btn-blue"
                                  onClick={() => {
                                    setRoleData(role)
                                    toggleModal()
                                    setModalType("view")
                                  }}
                                >
                                  <FaEye size={14} />
                                </Button>
                                {checkAccess(10, 4) && (
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => {
                                      setRoleData(role)
                                      toggleModal()
                                      setModalType("")
                                    }}
                                  >
                                    <FaPen size={14} />
                                  </Button>
                                )}
                                {checkAccess(10, 5) && (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDelete(role.id)}
                                  >
                                    <MdDelete size={14} />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              No role found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination Controls */}
                  <div className="d-flex justify-content-between  gap-2">
                    <p className="mb-0">
                      Showing {currentRoles.length} out of {roles.length} Roles.
                    </p>
                    <div>
                      <Button
                        size="sm"
                        disabled={currentPage === 1}
                        className="fs-6"
                        onClick={() => setCurrentPage(prev => prev - 1)}
                      >
                        <FaChevronCircleLeft />
                      </Button>
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          className="mx-2 fs-6"
                          color={currentPage === i + 1 ? "primary" : "light"}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        className="fs-6"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                      >
                        <FaChevronCircleRight />
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal */}
        <RoleModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          roleData={roleData}
          onChange={handleChange}
          onSave={handleSave}
          type={modalType}
        />
      </PermittedComponent>
    </div>
  )
}

export default RolesIndex
